//@flow
import Model from '../../Utils/Model/model';
import QuoteItemsCollection from '../../Stores/Quote/quoteItemsCollection';
import ProductsCollection from '../../Stores/Products/productsCollection';
import { getSubDiscountName } from '../../Utils/sub2save';

type CalculatedTotals = {
  regularSubtotal:?number,
  memberSubtotal:?number,
  virtualSubtotal:?number,
};

type TotalObject = {
  code:string,
  name:string,
  sort:number,
  value:number,
};

type DiscountDetails = {
  code:?string,
  name:string,
  coupon_type:number,
  description:?string,
  value:number,
  product_ids:string[],
  rule_id:string,
};

export type QuoteTotalsObject = {
  free_shipping_threshold:number,
  discount_details?:DiscountDetails[],
  discount?:TotalObject,
  grand_total:TotalObject,
  customerbalance?:TotalObject,
  shipping?:TotalObject,
  tax?:TotalObject,
  subtotal:TotalObject,
  calculated?:CalculatedTotals,
};

function _getValue(attr:?TotalObject):?number {
  return (attr && attr.value);
}

function _difference(num1:?number, num2:?number):?number {
  return num1 && num2 && (num1 - num2);
}

export default class QuoteTotalsModel extends Model<QuoteTotalsObject> {
  calculated:CalculatedTotals;

  constructor(attributes:$Shape<QuoteTotalsObject> = {}, items?:QuoteItemsCollection, products?:ProductsCollection) {
    super(attributes);
    this.calculated = attributes.calculated || _generateCalculatedTotals(items, products);
  }

  getGrandTotal():?number {
    return _getValue(this.attributes.grand_total);
  }

  // Calculates 10% of subtotal and rounds to 2 decimal places
  getEstimatedSubSavings():?number {
    const subtotal = this.getSubtotal();
    if (!subtotal) return;
    return (Math.round(subtotal * 0.1 * 100) / 100);
  }

  getShipping():?number {
    return _getValue(this.attributes.shipping);
  }

  getTax():?number {
    return _getValue(this.attributes.tax);
  }

  getSubtotal():?number {
    return _getValue(this.attributes.subtotal);
  }

  getRegularSubtotal():?number {
    return this.calculated.regularSubtotal;
  }

  getVirtualSubtotal():number {
    return this.calculated.virtualSubtotal || 0;
  }

  getMemberSubtotal():?number {
    return this.calculated.memberSubtotal;
  }

  getMemberSavings():?number {
    return _difference(this.getRegularSubtotal(), this.getSubtotal());
  }

  getMsrpMemberSavings():?number {
    return _difference(this.getRegularSubtotal(), this.getMemberSubtotal());
  }

  getSavings():number {
    const memberSavings = this.getMemberSavings() || 0;
    const discount = this.getDiscount() || 0;
    return Math.abs(memberSavings) + Math.abs(discount);
  }

  //guarantee that the discount is always a negative value
  getDiscount():number {
    const discount = _getValue(this.attributes.discount) || 0;
    return discount > 0 ? -1 * discount : discount;
  }

  getDiscountName():?string {
    const fullName = this.attributes.discount && this.attributes.discount.name;
    if (!fullName) return undefined;

    // match anything inside the parentheses
    const promoName = fullName.match(/\((.+)\)/);
    return promoName ? promoName[1] : undefined;
  }

  getDiscountDetails():?DiscountDetails[] {
    return this.attributes.discount_details;
  }

  getAppliedDiscount():?DiscountDetails {
    const discountDetails = this.getDiscountDetails();
    if (!discountDetails) return undefined;
    return discountDetails.find(discount => discount.coupon_type === 2);
  }

  getSub2SaveDiscount():?DiscountDetails {
    const discountDetails = this.getDiscountDetails();
    if (!discountDetails) return undefined;
    return discountDetails.find(discount => discount.name === getSubDiscountName());
  }

  //ignore virtual items for the recurring total
  getSubBoxRecurringTotal():number {
    const subDiscount = this.getSub2SaveDiscount();
    const subtotal = this.getSubtotal();
    const virtualSubtotal = this.getVirtualSubtotal() || 0;
    if (!subtotal || !subDiscount) return 0;

    const subDiscountVal = subDiscount.value || 0;
    return subtotal - virtualSubtotal + subDiscountVal;
  }

  getCustomerBalance():?number {
    return _getValue(this.attributes.customerbalance);
  }

  getAmountToFreeShipping(divisor:number = 1):?number {
    const subtotal = this.getSubtotal();
    const shipping = this.getShipping();

    if (subtotal === undefined || shipping === undefined || subtotal === null) return undefined;
    if (!shipping) return 0;

    const minimumFreeShipping = this.getMinimumFreeShipping();
    if (!minimumFreeShipping) return;
    //Ignore virtual products
    const subtotalLessVirtual = this.getSubtotalLessDiscounts() - this.getVirtualSubtotal();
    const amount = minimumFreeShipping - subtotalLessVirtual;

    return amount <= 0 ? 0 : amount/divisor;
  }

  getSubtotalLessDiscounts() {
    const subtotal = this.getSubtotal() || 0;
    //Remove the discount to revert back to pre discount shipping amount
    const discount = (-1 * (this.getDiscount() || 0));
    const subtotalLessDiscount = subtotal - discount;
    return subtotalLessDiscount;
  }

  getMinimumFreeShipping():number {
    const fst = this.attributes.free_shipping_threshold;
    return fst || fst === 0 ? fst : 40;
  }
}

function _generateCalculatedTotals(items:?QuoteItemsCollection, products:?ProductsCollection):CalculatedTotals {
  return {
    regularSubtotal: items && products && _calculateRegularSubtotal(items, products),
    memberSubtotal: items && products && _calculateMemberPrice(items, products),
    virtualSubtotal: items && _calculateVirtualSubtotal(items),
  };
}

// --- Private Helpers ---
function _calculateVirtualSubtotal(items:QuoteItemsCollection):?number {
  const total = items.modelsAttrs.reduce((prev, item) => {
    if (item.product_type === 'virtual' || item.product_type === 'giftvoucher') return prev + item.row_total;
    return 0;
  }, 0);
  return total;
}

/*
  Regular subtotal should be the subtotal an user would pay if it wasn't
  a member, normally this would be the msrp sum of all the items but some
  products lack and msrp attribute so in that case we use the real quote
  item price (no diference between a non-member and a member for the price).
*/
function _calculateRegularSubtotal(items:QuoteItemsCollection, products:ProductsCollection):?number {
  const total = items.modelsAttrs.reduce((prev, item) => {
    const product = products.getProductBySku(item.sku);
    if (!product || item.product_type === 'giftvoucher') return prev + item.row_total;

    const msrpTotal = (product.getMsrp() || 0) * item.qty;

    // Regular sub total needs to be bigger than member sub total.
    if (msrpTotal < item.row_total) return prev + item.row_total;

    return prev + msrpTotal;
  }, 0);

  return total;
}

function _calculateMemberPrice(items:QuoteItemsCollection, products:ProductsCollection):?number {
  return products.getTotalMemberPrice(items.modelsAttrs);
}
